<template>
  <div class="hotel-list">
    <simple-list
      id="hotelCode"
      ref="hotelCode"
      :server-items="hotels"
      query="$and.0.propertyRegion"
      :undefined-value="tc('booking.allRegions')"
      class="req"
      variant="hotels-list"
      internal-scroll
      :height="listHeight"
      @item-update="getFocusableElements"
      @mounted="getFocusableElements"
    >
      <template #filter>
        <div
          ref="hotelListHeader"
          class="hotel-list-header"
        >
          <header-close :deactivate="deactivate" />
          <hotel-list-search-bar @filter-items="filterItems" />
          <hotel-list-regions @filter-items="filterItems" />
        </div>
      </template>
      <template #rendered-list="{ items }">
        <ul>
          <li
            v-for="item in items"
            :key="item.id"
          >
            <hotel-list-card
              :item="item"
              :search-input="inputString"
              :query-changed="queryChanged"
              :is-loading="isLoading"
              @hotel-selected="updateFilter"
            />
          </li>
        </ul>
      </template>
    </simple-list>
    <loading-dots />
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import TabTrapMixin from '@mixins/tab-trap'
  import { HotelListSearchBar, HotelListRegions, HotelListCard } from './components'
  import HeaderClose from '../header-close/header-close.vue'

  export default {
    name: 'HotelList',
    components: {
      HotelListSearchBar,
      HotelListRegions,
      HotelListCard,
      HeaderClose,
    },
    mixins: [TabTrapMixin],
    inject: ['updateLocalQuery'],
    props: {
      runRouteValidation: {
        type: Function,
        required: true,
      },
      deactivate: {
        type: Function,
        required: true,
      }
    },
    data: () => ({
      isLoading: false,
      listHeight: null,
      inputString: '',
      inputValue: { $exists: true },
      tabValue: { $exists: true },
      queryChanged: -1
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('hotelData', ['getHotelById']),
      ...mapGetters('currency', ['orSelected']),
      ...mapGetters('booking', ['getBookingTrackingV2', 'getQuery']),
      ...mapState('booking', ['step']),
      ...mapState('hotelData', ['hotels']),
      ...mapState('responsive', { wHeight: 'height' }),
    },
    watch: {
      wHeight() {
        this.calculateListHeight()
      }
    },
    mounted() {
      this.calculateListHeight()

      this.$track(this.getBookingTrackingV2(this.getQuery, {
        event: 'bookingStateChange',
        bookingEngine: {
          bookingStepNumber: '1.1',
          bookingStepName: 'Location Selection Flyout',
          bookingEngineVersion: '1'
        }
      }))
      
      if (window.adobe?.target?.triggerView) {
        window.adobe.target.triggerView('Location Selection Flyout')
      }
    },
    methods: {
      ...mapActions('currency', ['getRate']),
      async updateFilter(data) {
        this.isLoading = true
        this.updateLocalQuery({ prop: 'hotelCode', value: data.id })
        const hotelCurrency = this.getHotelById(data.id)?.currency?.isoCode
        if (hotelCurrency) {
          await this.getRate({ fromCurrency: hotelCurrency, toCurrency: this.orSelected(hotelCurrency) })
        }
        this.$emit('change-page', 'calendar')
      },
      calculateListHeight() {
        const headerHeight = this.$refs.hotelListHeader.clientHeight
        this.listHeight = this.wHeight - headerHeight
      },
      closeModal() {
        this.deactivate()
      },
      async filterItems(value, type) {
        if (type === 'input') {
          if (value) {
            this.inputValue = { $regex: `${value}`, $options: 'i' }
            this.inputString = value
          } else {
            this.inputValue = { $exists: true }
            this.inputString = ''
          }
        }
        if (type === 'tab') {
          if (value) {
            this.tabValue = value
          } else {
            this.tabValue = { $exists: true }
          }
        }
        const searchFilter = {
          $and: [
            {
              propertyRegion: this.tabValue
            },
            {
              $or: [
                { propertyName: this.inputValue },
                { country: this.inputValue },
              ]
            }
          ]
        }
        await this.$refs.hotelCode.updateFilter(searchFilter)
        this.queryChanged++
        this.getFocusableElements()
      },
    },
  }
</script>
